/**
 * generatePlayerProfilePDF — produces an A4 PDF "Fiche apprenant" for the
 * pedagogical reviewer (Super Vue admin).
 *
 * Layout (single page):
 *   ┌────────────────────────────────────────┐
 *   │ Header (logo SCD + logo société + nom) │
 *   ├────────────────────────────────────────┤
 *   │ Stats clés (treasury, equity, rep, …) │
 *   ├──────────────────────┬─────────────────┤
 *   │ Radar 8 axes         │ Tableau /10     │
 *   ├──────────────────────┴─────────────────┤
 *   │ Typologie (label + résumé)             │
 *   ├────────────────────────────────────────┤
 *   │ Analyse pédagogique (4-5 lignes)       │
 *   ├────────────────────────────────────────┤
 *   │ Points forts | Axes d'amélioration     │
 *   └────────────────────────────────────────┘
 *
 * 04/06/2026 — first version, sprint feedback hst-game.com.
 */
import { jsPDF } from 'jspdf';
import computePlayerTypology, { AXIS_LABELS, AXIS_LABELS_SHORT, AXIS_ORDER } from './computePlayerTypology';
import { formatMoneyForPdf, normalizePdfSpaces } from './formatMoneyForPdf';

const COLORS = {
  primary: '#1F3864',
  accent: '#3B82F6',
  text: '#1F2937',
  muted: '#6B7280',
  light: '#E5E7EB',
  positive: '#059669',
  negative: '#DC2626',
};

function fmtMoney(v) {
  return formatMoneyForPdf(v);
}

function fmtScore(v) {
  return `${v}/10`;
}

/** Draw the radar chart on a jsPDF canvas. */
function drawRadar(doc, cx, cy, radius, scores) {
  const axisCount = AXIS_ORDER.length;
  const angleStep = (Math.PI * 2) / axisCount;
  const angleStart = -Math.PI / 2; // 12 o'clock

  // Grid (5 concentric polygons + radial lines).
  doc.setDrawColor(229, 231, 235);
  doc.setLineWidth(0.2);
  for (let r = 1; r <= 5; r++) {
    const rr = (radius * r) / 5;
    const points = [];
    for (let i = 0; i < axisCount; i++) {
      const a = angleStart + i * angleStep;
      points.push([cx + Math.cos(a) * rr, cy + Math.sin(a) * rr]);
    }
    strokePolygon(doc, points);
  }
  // Radial spokes.
  for (let i = 0; i < axisCount; i++) {
    const a = angleStart + i * angleStep;
    doc.line(cx, cy, cx + Math.cos(a) * radius, cy + Math.sin(a) * radius);
  }

  // Data polygon.
  const dataPts = AXIS_ORDER.map((k, i) => {
    const v = scores[k] || 0;
    const rr = (radius * v) / 10;
    const a = angleStart + i * angleStep;
    return [cx + Math.cos(a) * rr, cy + Math.sin(a) * rr];
  });
  // Stroke the data polygon thick (no fill — simpler & always readable).
  doc.setDrawColor(31, 56, 100);
  doc.setLineWidth(0.8);
  strokePolygon(doc, dataPts);

  // Data points.
  doc.setFillColor(31, 56, 100);
  dataPts.forEach(([x, y]) => {
    doc.circle(x, y, 1.0, 'F');
  });

  // Axis labels.
  doc.setTextColor(31, 41, 55);
  doc.setFontSize(7);
  AXIS_ORDER.forEach((k, i) => {
    const a = angleStart + i * angleStep;
    const lx = cx + Math.cos(a) * (radius + 6);
    const ly = cy + Math.sin(a) * (radius + 6) + 1;
    // 28/07/2026 — libellés courts : les libellés longs (« Diplomatie &
    // Relations externes ») débordaient du bord gauche de la page.
    const label = AXIS_LABELS_SHORT[k] || AXIS_LABELS[k];
    const align = Math.cos(a) > 0.3 ? 'left' : Math.cos(a) < -0.3 ? 'right' : 'center';
    doc.text(label, lx, ly, { align });
  });
}

function strokePolygon(doc, points) {
  if (points.length === 0) return;
  for (let i = 0; i < points.length; i++) {
    const [x1, y1] = points[i];
    const [x2, y2] = points[(i + 1) % points.length];
    doc.line(x1, y1, x2, y2);
  }
}

/* ─── Page 2 : Bilan économique de la partie (28/07/2026) ─────────────── */

const POSTE_LABELS = {
  salaires: 'Salaires chauffeurs',
  fonctionsSupport: 'Fonctions support',
  managersDepots: 'Managers de dépôts',
  carburant: 'Carburant',
  peages: 'Péages',
  entretien: 'Entretien',
  assurances: 'Assurances',
  leasing: 'Leasing',
  credits: 'Crédits',
  investissements: 'Investissements',
  loyersEtDepots: 'Loyers & dépôts',
  autres: 'Autres',
};

function drawSectionTitle(doc, title, M, y) {
  doc.setFontSize(9.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(31, 56, 100);
  doc.text(title, M, y);
  doc.setFont('helvetica', 'normal');
  return y + 5;
}

function drawEconomicsPage(doc, snapshot, license, W, M) {
  doc.addPage();
  doc.setFillColor(31, 56, 100);
  doc.rect(0, 0, W, 22, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Bilan économique de la partie', M, 10);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text([
    license?.holder_name || snapshot.company_name || 'Apprenant·e',
    `J${snapshot.total_days || 0}`,
    `An ${snapshot.year || 1}`,
  ].join(' · '), M, 16);

  let y = 32;

  /* ── 1. Graphique CA / Charges / Résultat net (12 derniers mois) ────── */
  y = drawSectionTitle(doc, 'CA, charges et résultat net — 12 derniers mois', M, y);
  const series = snapshot.monthly_series || [];
  if (series.length === 0) {
    doc.setFontSize(8);
    doc.setTextColor(107, 114, 128);
    doc.text('Pas encore de mois clos — le graphique apparaîtra dès la première clôture mensuelle.', M, y + 2);
    y += 10;
  } else {
    const chartX = M + 12;
    const chartW = W - 2 * M - 14;
    const chartH = 52;
    const maxVal = Math.max(1, ...series.map(s => Math.max(s.revenue || 0, s.expenses || 0, s.net || 0)));
    const minVal = Math.min(0, ...series.map(s => s.net || 0));
    const span = maxVal - minVal;
    const yFor = (v) => y + chartH - ((v - minVal) / span) * chartH;
    // Grille + axe Y (3 repères).
    doc.setDrawColor(229, 231, 235);
    doc.setLineWidth(0.2);
    doc.setFontSize(6);
    doc.setTextColor(107, 114, 128);
    [maxVal, (maxVal + minVal) / 2, minVal].forEach(v => {
      const gy = yFor(v);
      doc.line(chartX, gy, chartX + chartW, gy);
      doc.text(`${Math.round(v / 1000)} k€`, chartX - 1.5, gy + 1, { align: 'right' });
    });
    // Ligne du zéro (si résultat négatif dans la fenêtre).
    if (minVal < 0) {
      doc.setDrawColor(156, 163, 175);
      doc.setLineWidth(0.3);
      doc.line(chartX, yFor(0), chartX + chartW, yFor(0));
    }
    const groupW = chartW / series.length;
    const barW = Math.min(5, groupW / 3);
    series.forEach((s, i) => {
      const gx = chartX + i * groupW + groupW / 2;
      // CA (vert) et charges (rouge) : barres depuis 0.
      doc.setFillColor(5, 150, 105);
      doc.rect(gx - barW - 0.5, yFor(s.revenue || 0), barW, yFor(0) - yFor(s.revenue || 0), 'F');
      doc.setFillColor(220, 38, 38);
      doc.rect(gx + 0.5, yFor(s.expenses || 0), barW, yFor(0) - yFor(s.expenses || 0), 'F');
      // Mois.
      doc.setFontSize(6);
      doc.setTextColor(107, 114, 128);
      doc.text(`M${s.month}`, gx, y + chartH + 3.5, { align: 'center' });
    });
    // Résultat net : ligne bleue reliant les points.
    doc.setDrawColor(37, 99, 235);
    doc.setLineWidth(0.6);
    for (let i = 1; i < series.length; i++) {
      const x1 = chartX + (i - 1) * groupW + groupW / 2;
      const x2 = chartX + i * groupW + groupW / 2;
      doc.line(x1, yFor(series[i - 1].net || 0), x2, yFor(series[i].net || 0));
    }
    doc.setFillColor(37, 99, 235);
    series.forEach((s, i) => {
      doc.circle(chartX + i * groupW + groupW / 2, yFor(s.net || 0), 0.9, 'F');
    });
    // Légende.
    const legend = [
      ['CA', [5, 150, 105]],
      ['Charges', [220, 38, 38]],
      ['Résultat net', [37, 99, 235]],
    ];
    let lx = chartX;
    const ly = y + chartH + 8;
    doc.setFontSize(7);
    legend.forEach(([lab, col]) => {
      doc.setFillColor(col[0], col[1], col[2]);
      doc.rect(lx, ly - 2.4, 3, 3, 'F');
      doc.setTextColor(55, 65, 81);
      doc.text(lab, lx + 4.5, ly);
      lx += doc.getTextWidth(lab) + 14;
    });
    y += chartH + 14;
  }

  /* ── 2. Répartition des charges (dernier mois clos) + aléas ─────────── */
  y = drawSectionTitle(doc, 'Répartition des charges — dernier mois clos', M, y);
  const postes = snapshot.expenses_by_poste;
  if (!postes) {
    doc.setFontSize(8);
    doc.setTextColor(107, 114, 128);
    doc.text('Disponible après la première clôture mensuelle.', M, y + 2);
    y += 8;
  } else {
    const entries = Object.entries(postes)
      .map(([k, v]) => [POSTE_LABELS[k] || k, v || 0])
      .filter(([, v]) => v > 0)
      .sort((a, b) => b[1] - a[1]);
    const totalPostes = entries.reduce((s, [, v]) => s + v, 0) || 1;
    const barMaxW = 90;
    doc.setFontSize(7.5);
    entries.forEach(([lab, v]) => {
      doc.setTextColor(55, 65, 81);
      doc.text(lab, M, y + 2.6);
      const bw = Math.max(0.8, (v / totalPostes) * barMaxW);
      doc.setFillColor(220, 38, 38);
      doc.rect(M + 42, y, bw, 3.2, 'F');
      doc.setTextColor(31, 41, 55);
      doc.setFont('helvetica', 'bold');
      doc.text(`${fmtMoney(v)} (${Math.round((v / totalPostes) * 100)} %)`, M + 44 + bw, y + 2.6);
      doc.setFont('helvetica', 'normal');
      y += 4.6;
    });
    y += 2;
  }
  // Aléas — cumul annuel (les événements ne sont pas un poste mensuel fixe).
  const aleas = snapshot.aleas_ytd || { cost: 0, count: 0 };
  doc.setFontSize(8);
  doc.setTextColor(180, 83, 9);
  doc.setFont('helvetica', 'bold');
  doc.text(
    `Aléas (année en cours) : ${fmtMoney(aleas.cost)} — ${aleas.count} événement(s) subi(s)`,
    M, y + 1,
  );
  doc.setFont('helvetica', 'normal');
  y += 8;

  /* ── 3. Poids des taxes (année en cours) ────────────────────────────── */
  y = drawSectionTitle(doc, 'Poids des taxes — année en cours', M, y);
  const taxes = snapshot.taxes_ytd || {};
  const taxCells = [
    ['TVA payée', taxes.tva],
    ['IS (acomptes payés)', taxes.is],
    ['Taxe à l\'essieu', taxes.essieu],
  ];
  const cellW = (W - 2 * M - 8) / 3;
  taxCells.forEach(([lab, v], i) => {
    const x = M + i * (cellW + 4);
    doc.setFillColor(243, 244, 246);
    doc.roundedRect(x, y, cellW, 13, 1.5, 1.5, 'F');
    doc.setFontSize(6.5);
    doc.setTextColor(107, 114, 128);
    doc.text(lab.toUpperCase(), x + 3, y + 4.5);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(31, 41, 55);
    doc.text(fmtMoney(v || 0), x + 3, y + 10);
    doc.setFont('helvetica', 'normal');
  });
  y += 19;

  /* ── 4. Contrats : honorés vs laissés filer par tranche ─────────────── */
  y = drawSectionTitle(doc, 'Contrats — honorés vs laissés filer (12 derniers mois, par tranche de valeur)', M, y);
  const recap = snapshot.contracts_recap;
  if (!recap || (recap.total_taken || 0) + (recap.total_missed || 0) === 0) {
    doc.setFontSize(8);
    doc.setTextColor(107, 114, 128);
    doc.text('Pas encore de contrats tracés sur la fenêtre annuelle.', M, y + 2);
    y += 8;
  } else {
    doc.setFontSize(7);
    doc.setTextColor(107, 114, 128);
    const cols = [M, M + 60, M + 95, M + 130, M + 158];
    ['TRANCHE', 'HONORÉS', 'LAISSÉS FILER', 'TAUX', ''].forEach((h, i) => doc.text(h, cols[i], y));
    y += 1.5;
    doc.setDrawColor(229, 231, 235);
    doc.line(M, y, W - M, y);
    y += 4;
    doc.setFontSize(8);
    (recap.rows || []).forEach(r => {
      doc.setTextColor(31, 41, 55);
      doc.text(r.label, cols[0], y);
      doc.setTextColor(5, 150, 105);
      doc.text(String(r.taken || 0), cols[1], y);
      doc.setTextColor(220, 38, 38);
      doc.text(String(r.missed || 0), cols[2], y);
      doc.setTextColor(31, 41, 55);
      doc.text(r.rate === null || r.rate === undefined ? '—' : `${r.rate} %`, cols[3], y);
      y += 5;
    });
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(31, 56, 100);
    doc.text(
      `Total : ${recap.total_taken || 0} honorés · ${recap.total_missed || 0} laissés filer · taux d'acceptation ${recap.acceptance_rate === null ? '—' : `${recap.acceptance_rate} %`}`,
      M, y + 1,
    );
    doc.setFont('helvetica', 'normal');
    y += 7;
  }

  /* Footer */
  doc.setFontSize(6);
  doc.setTextColor(156, 163, 175);
  doc.text(
    'Bilan économique calculé sur la télémétrie de la partie sélectionnée. Confidentiel — usage pédagogique exclusivement.',
    W / 2, 290, { align: 'center' },
  );
}

/* ─── Smart recommendations page ──────────────────────────────────────── */

const REC_STYLE = {
  critical:    { fill: [254, 226, 226], border: [220,  38,  38], text: [127,  29,  29], badge: 'CRITIQUE',     icon: '!' },
  warning:     { fill: [254, 243, 199], border: [217, 119,   6], text: [120,  53,  15], badge: 'VIGILANCE',    icon: '!' },
  opportunity: { fill: [219, 234, 254], border: [ 37,  99, 235], text: [ 30,  64, 175], badge: 'OPPORTUNITÉ',  icon: '+' },
  tip:         { fill: [220, 252, 231], border: [  5, 150, 105], text: [  6,  78,  59], badge: 'CONSEIL',      icon: '·' },
};

function drawRecommendationsPage(doc, recommendations, license, snapshot, W, M) {
  if (!recommendations || recommendations.length === 0) return;
  doc.addPage();

  /* Header bar (same look as page 1 for consistency) */
  doc.setFillColor(31, 56, 100);
  doc.rect(0, 0, W, 22, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Recommandations stratégiques', M, 10);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  const subHeader = [
    license?.holder_name || snapshot.company_name || 'Apprenant·e',
    `J${snapshot.total_days || 0}`,
    `An ${snapshot.year || 1}`,
  ].filter(Boolean).join(' · ');
  doc.text(subHeader, M, 16);

  let y = 32;

  /* Intro */
  doc.setTextColor(75, 85, 99);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  const intro = doc.splitTextToSize(
    "Conseils contextuels générés à partir de votre état de jeu actuel. Priorisez les recommandations critiques (rouge) avant tout, puis les vigilances (orange) ; les opportunités (bleu) et conseils (vert) sont des leviers d'optimisation.",
    W - 2 * M,
  );
  doc.text(intro, M, y);
  y += intro.length * 4 + 4;

  /* Cards */
  recommendations.forEach((rec) => {
    const style = REC_STYLE[rec.level] || REC_STYLE.tip;
    const bodyLines = doc.splitTextToSize(normalizePdfSpaces(rec.body), W - 2 * M - 8);
    const cardH = 12 + bodyLines.length * 4 + 4;

    // Card background
    doc.setFillColor(...style.fill);
    doc.setDrawColor(...style.border);
    doc.setLineWidth(0.4);
    doc.roundedRect(M, y, W - 2 * M, cardH, 2.5, 2.5, 'FD');

    // Left accent bar
    doc.setFillColor(...style.border);
    doc.rect(M, y, 2, cardH, 'F');

    // Badge (level)
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...style.border);
    doc.text(style.badge, M + 6, y + 5);

    // Title
    doc.setFontSize(10.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...style.text);
    doc.text(normalizePdfSpaces(rec.title), M + 6, y + 10);

    // Body
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(55, 65, 81);
    doc.text(bodyLines, M + 6, y + 15);

    y += cardH + 3;
  });

  /* Footer */
  doc.setFontSize(6);
  doc.setTextColor(156, 163, 175);
  doc.text(
    'Recommandations rule-based — déterministes et reproductibles. À discuter avec votre formateur·rice pour approfondir.',
    W / 2, 290, { align: 'center' },
  );
}

/* ─── Public API ──────────────────────────────────────────────────────── */

/**
 * Generate the PDF in memory and return either a Blob or trigger download.
 *
 * @param {Object} opts
 * @param {Object} opts.snapshot    - Telemetry snapshot of the player
 * @param {Object} [opts.license]   - { holder_name, holder_email }
 * @param {boolean} [opts.download=true] - if true, triggers a save(); else returns the Blob
 * @returns {Blob|undefined} A Blob when download=false, undefined otherwise.
 */
export default function generatePlayerProfilePDF(opts = {}) {
  const { snapshot, license, download = true } = opts;
  if (!snapshot) throw new Error('generatePlayerProfilePDF: snapshot is required');

  const { scores, typology, analysis, highlights, recommendations } = computePlayerTypology(snapshot);

  // A4 page support — single-page or two-page depending on rec count.
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const W = 210;
  const M = 15; // page margin

  /* ── Header bar ────────────────────────────────────────────────────── */
  doc.setFillColor(31, 56, 100);
  doc.rect(0, 0, W, 22, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('SCD — Fiche apprenant', M, 10);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text(
    `Édité le ${new Date().toLocaleDateString('fr-FR')} · Société de Commerce et de Distribution`,
    M, 16,
  );

  let y = 30;

  /* ── Player identity ───────────────────────────────────────────────── */
  doc.setTextColor(31, 41, 55);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(license?.holder_name || snapshot.company_name || 'Apprenant·e', M, y);
  y += 6;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(107, 114, 128);
  const DIFF_LABELS = { facile: 'Facile', normal: 'Normal', difficile: 'Difficile' };
  const sub = [
    snapshot.company_name && `Partie : ${snapshot.company_name}`,
    `Difficulté : ${DIFF_LABELS[snapshot.difficulty] || snapshot.difficulty || 'Normal'}`,
    snapshot.company_city && `Ville : ${snapshot.company_city}`,
    license?.holder_email && `Email : ${license.holder_email}`,
  ].filter(Boolean).join(' · ');
  doc.text(sub || '—', M, y);
  y += 8;

  /* ── Stats key/value grid (4 columns × 2 rows) ─────────────────────── */
  doc.setDrawColor(229, 231, 235);
  doc.setLineWidth(0.2);
  doc.line(M, y, W - M, y);
  y += 4;

  // 28/07/2026 — le compteur du mois EN COURS est partiel (remis à zéro à
  // chaque début de mois) : il faisait apparaître un « CA mensuel » plus
  // faible que les charges. On affiche le dernier mois CLOS quand il existe.
  const lm = snapshot.last_month;
  const stats = [
    { label: 'Jour', value: `J${snapshot.total_days || 0} · An ${snapshot.year || 1}` },
    { label: 'Phase', value: `Phase ${snapshot.phase || 1}` },
    { label: 'Trésorerie', value: fmtMoney(snapshot.treasury) },
    { label: 'Capitaux propres', value: fmtMoney(snapshot.equity) },
    { label: lm ? 'CA (dernier mois clos)' : 'CA mensuel (en cours)', value: fmtMoney(lm ? lm.revenue : snapshot.monthly_revenue) },
    { label: lm ? 'Charges (dernier mois clos)' : 'Charges (mois en cours)', value: fmtMoney(lm ? lm.expenses : snapshot.monthly_expenses) },
    { label: 'Réputation', value: `${snapshot.reputation || 0}/100` },
    { label: 'Notoriété', value: `${snapshot.notoriety || 0}/100` },
    { label: 'Flotte', value: `${snapshot.fleet_total || 0} véhicules (${snapshot.fleet_active || 0} actifs)` },
    { label: 'Équipe', value: `${snapshot.drivers_total || 0} chauffeurs · ${snapshot.support_total || 0} support` },
    { label: 'Crédits', value: `${snapshot.credits_count || 0} (${fmtMoney(snapshot.credits_total_remaining || 0)})` },
    { label: 'Contrats actifs', value: `${snapshot.contracts_active || 0} · ${snapshot.contracts_completed_lifetime || 0} clôturés` },
  ];
  const colW = (W - 2 * M) / 4;
  stats.forEach((stat, i) => {
    const col = i % 4;
    const row = Math.floor(i / 4);
    const x = M + col * colW;
    const yy = y + row * 11;
    doc.setFontSize(7);
    doc.setTextColor(107, 114, 128);
    doc.text(stat.label.toUpperCase(), x, yy);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(31, 41, 55);
    doc.text(String(stat.value), x, yy + 5);
    doc.setFont('helvetica', 'normal');
  });
  y += Math.ceil(stats.length / 4) * 11 + 4;

  /* ── Radar + table side by side ─────────────────────────────────────── */
  const radarCx = M + 38;
  const radarCy = y + 35;
  drawRadar(doc, radarCx, radarCy, 30, scores);

  // Score table (right side).
  const tx = M + 90;
  const ty = y + 4;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(31, 41, 55);
  doc.text('Évaluation par axe', tx, ty);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  AXIS_ORDER.forEach((k, i) => {
    const yy = ty + 6 + i * 6;
    doc.setTextColor(31, 41, 55);
    doc.text(AXIS_LABELS[k], tx, yy);
    // Bar.
    const barX = tx + 50;
    const barW = 35;
    doc.setFillColor(229, 231, 235);
    doc.rect(barX, yy - 3, barW, 3.2, 'F');
    const fillW = barW * (scores[k] / 10);
    doc.setFillColor(59, 130, 246);
    doc.rect(barX, yy - 3, fillW, 3.2, 'F');
    // Value.
    doc.setFont('helvetica', 'bold');
    doc.text(fmtScore(scores[k]), barX + barW + 3, yy);
    doc.setFont('helvetica', 'normal');
  });
  y = radarCy + 38;

  /* ── Typology callout ──────────────────────────────────────────────── */
  doc.setFillColor(243, 244, 246);
  doc.roundedRect(M, y, W - 2 * M, 18, 2, 2, 'F');
  doc.setFontSize(8);
  doc.setTextColor(107, 114, 128);
  doc.text('TYPOLOGIE DE JOUEUR', M + 3, y + 4);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(31, 56, 100);
  doc.text(typology.label, M + 3, y + 10);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(75, 85, 99);
  const sumLines = doc.splitTextToSize(normalizePdfSpaces(typology.summary), W - 2 * M - 6);
  doc.text(sumLines, M + 3, y + 14);
  y += 22;

  /* ── Analysis paragraph ─────────────────────────────────────────────── */
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(31, 41, 55);
  doc.text('Analyse pédagogique', M, y);
  y += 5;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(55, 65, 81);
  analysis.forEach(line => {
    const wrapped = doc.splitTextToSize(normalizePdfSpaces(line), W - 2 * M);
    doc.text(wrapped, M, y);
    y += wrapped.length * 4 + 1;
  });
  y += 3;

  /* ── Highlights side-by-side ────────────────────────────────────────── */
  const halfW = (W - 2 * M - 4) / 2;
  // Positives
  doc.setFillColor(220, 252, 231);
  doc.roundedRect(M, y, halfW, 4 + Math.max(highlights.positives.length, 1) * 5, 1.5, 1.5, 'F');
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(5, 150, 105);
  doc.text('POINTS FORTS', M + 2, y + 4);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(31, 41, 55);
  if (highlights.positives.length === 0) {
    doc.text('—', M + 2, y + 9);
  } else {
    highlights.positives.forEach((p, i) => {
      doc.text(`• ${normalizePdfSpaces(p)}`, M + 2, y + 9 + i * 5);
    });
  }
  // Improvements
  const ix = M + halfW + 4;
  doc.setFillColor(254, 226, 226);
  doc.roundedRect(ix, y, halfW, 4 + Math.max(highlights.improvements.length, 1) * 5, 1.5, 1.5, 'F');
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(220, 38, 38);
  doc.text("AXES D'AMÉLIORATION", ix + 2, y + 4);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(31, 41, 55);
  if (highlights.improvements.length === 0) {
    doc.text('—', ix + 2, y + 9);
  } else {
    highlights.improvements.forEach((p, i) => {
      doc.text(`• ${normalizePdfSpaces(p)}`, ix + 2, y + 9 + i * 5);
    });
  }

  /* ── Footer ─────────────────────────────────────────────────────────── */
  doc.setFontSize(6);
  doc.setTextColor(156, 163, 175);
  doc.text(
    'Document généré automatiquement à partir de la dernière télémétrie disponible. Confidentiel — usage pédagogique exclusivement.',
    W / 2, 290, { align: 'center' },
  );

  /* ── Page 2 : Bilan économique (28/07/2026) ──────────────────────────── */
  drawEconomicsPage(doc, snapshot, license, W, M);

  /* ── Page 3 : Smart recommendations (always emitted) ─────────────────── */
  drawRecommendationsPage(doc, recommendations, license, snapshot, W, M);

  /* ── Output ────────────────────────────────────────────────────────── */
  const filename = `hst-fiche-${(license?.holder_name || snapshot.company_name || 'apprenant')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .slice(0, 40)}.pdf`;

  if (download) {
    doc.save(filename);
    return undefined;
  }
  return doc.output('blob');
}
